import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import AnimatedSection from "../common/AnimatedSection";
import SectionHeading from "../common/SectionHeading";
import Button from "../common/Button";
import edgeImage from "../../assets/images/EDGE.jpg";

const projects = [
  {
    id: 1,
    title: "Modern 3BHK",
    location: "Kondapur",
    type: "3BHK Apartment",
    image:
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=600&q=80",
    tags: ["Kitchen", "Wardrobes", "Living"],
  },
  {
    id: 2,
    title: "Luxury Villa",
    location: "Narsingi",
    type: "Villa",
    image:
      "https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=600&q=80",
    tags: ["Full Home", "Premium"],
  },
  {
    id: 3,
    title: "Compact 2BHK",
    location: "Gachibowli",
    type: "2BHK Apartment",
    image:
      "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=600&q=80",
    tags: ["Rental Package"],
  },
  {
    id: 4,
    title: "Premium Kitchen",
    location: "Madhapur",
    type: "Kitchen Only",
    image:
      "https://images.unsplash.com/photo-1556911220-bff31c812dba?w=600&q=80",
    tags: ["Kitchen", "Island"],
  },
  {
    id: 5,
    title: "Builder Project",
    location: "Kukatpally",
    type: "B2B",
    image:
      "https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=600&q=80",
    tags: ["Model House", "Bulk"],
  },
  {
    id: 6,
    title: "Spacious 4BHK",
    location: "Banjara Hills",
    type: "4BHK Apartment",
    image:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80",
    tags: ["Full Home", "Luxury"],
  },
  {
    id: 7,
    title: "Spacious 4BHK",
    location: "Banjara Hills",
    type: "4BHK Apartment",
    image: "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?q=80",
    tags: ["Full Home", "Luxury"],
  },
  {
    id: 8,
    title: "Spacious 4BHK",
    location: "Banjara Hills",
    type: "4BHK Apartment",
    image: "https://images.unsplash.com/photo-1748957368338-ff3224693be3?q=80",
    tags: ["Full Home", "Luxury"],
  },
];

export default function GalleryPreview() {
  return (
    <section className="section-spacing bg-white">
      <div className="container-custom section-padding">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <SectionHeading
            label="See What's Possible"
            title="Real homes we've transformed."
            align="left"
            className="mb-0"
          />
          <Button to="/gallery" variant="secondary">
            View full gallery
          </Button>
        </div>

        {/* Premium Asymmetric Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Large Featured Project - Left */}
          <AnimatedSection delay={0} className="md:col-span-7">
            <Link
              to={`/gallery?project=${projects[0].id}`}
              className="group block relative rounded-3xl overflow-hidden shadow-soft hover:shadow-large transition-all duration-500"
            >
              <div className="w-full aspect-[4/5]">
                <img
                  src={projects[0].image}
                  alt={projects[0].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              </div>

              <div className="absolute top-6 left-6 flex flex-wrap gap-2">
                {projects[0].tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-2 bg-brand-accent text-white backdrop-blur-sm rounded-full text-xs font-semibold uppercase tracking-wide"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h4 className="font-heading text-3xl text-white mb-2">
                  {projects[0].title}
                </h4>
                <p className="text-white/90 text-base mb-4">
                  {projects[0].type} • {projects[0].location}
                </p>
                <div className="flex items-center gap-2 text-white/80 group-hover:text-brand-accent transition-colors">
                  <span className="text-sm font-medium">View Project</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          </AnimatedSection>

          {/* Right Column - Stacked Projects */}
          <div className="md:col-span-5 space-y-6">
            {projects.slice(1, 3).map((project, index) => (
              <AnimatedSection key={project.id} delay={(index + 1) * 100}>
                <Link
                  to={`/gallery?project=${project.id}`}
                  className="group block relative rounded-3xl overflow-hidden shadow-soft hover:shadow-large transition-all duration-500"
                >
                  <div className="w-full aspect-[16/10]">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  </div>

                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-semibold text-brand-primary"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h4 className="font-heading text-xl text-white mb-1">
                      {project.title}
                    </h4>
                    <p className="text-white/80 text-sm">
                      {project.type} • {project.location}
                    </p>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>

          {/* Bottom Row - Three Equal Projects */}
          {projects.slice(3, 6).map((project, index) => (
            <AnimatedSection
              key={project.id}
              delay={(index + 3) * 100}
              className="md:col-span-4"
            >
              <Link
                to={`/gallery?project=${project.id}`}
                className="group block relative rounded-3xl overflow-hidden shadow-soft hover:shadow-large transition-all duration-500"
              >
                <div className="w-full aspect-[4/3]">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                </div>

                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-semibold text-brand-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h4 className="font-heading text-lg text-white mb-1">
                    {project.title}
                  </h4>
                  <p className="text-white/80 text-sm">
                    {project.type} • {project.location}
                  </p>
                </div>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
